import { useCallback, useEffect, useRef, useState } from 'react';
import { wbApi, wbWsUrl, wbError } from './wbApi';

/* Estado do jogador (v2): saldo de Moedas da Bruxa, feed ao vivo e estourar balão (idempotente por client_request_id).
   Tempo real via WebSocket; vigia reconecta socket mudo e confere /state a cada 15 s. */
export const useWbGame = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [feed, setFeed] = useState([]);
  const [conectado, setConectado] = useState(false);
  const [rankTick, setRankTick] = useState(0);
  const [goldenWin, setGoldenWin] = useState(null);
  const ws = useRef(null);
  const tentativas = useRef(0);
  const vivo = useRef(true);
  const ultimoSinal = useRef(Date.now());

  const load = useCallback(async () => {
    try {
      const { data } = await wbApi.get('/state');
      setState(data);
      setErro('');
      if (data?.feed) setFeed(data.feed);
      return data;
    } catch (e) {
      setErro(wbError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const onEvent = useCallback((msg) => {
    const { type, data = {} } = msg || {};
    if (!type || type === 'wb.hello' || type === 'wb.pong') return;
    if (msg.text) setFeed((f) => [{ type, text: msg.text, data, at: msg.at }, ...f].slice(0, 60));
    if (type === 'wb.witch_coins' && data.balance != null) {
      setState((s) => (s?.me ? { ...s, me: { ...s.me, balance: Number(data.balance), earned: Number(s.me.earned || 0) + Math.max(0, Number(data.witch_coins || 0)),
        pops_available: Math.floor(Number(data.balance) / Math.max(1, s.config?.pop_cost || 500)) } } : s));
    }
    if (type === 'wb.win_big') setGoldenWin({ id: msg.at || Date.now(), nickname: data.nickname, avatar: data.avatar || '', prize: Number(data.prize || 0) });
    if (type === 'wb.win_big' || type === 'wb.win_small' || type === 'wb.pop') setRankTick((t) => t + 1);
    if (type.startsWith('wb.event.')) load();
  }, [load]);

  const connect = useCallback(() => {
    if (!vivo.current) return;
    try { ws.current?.close(); } catch (e) { /* ignora */ }
    const s = new WebSocket(wbWsUrl());
    ws.current = s;
    s.onopen = () => { if (ws.current !== s) return; if (tentativas.current > 0) load(); tentativas.current = 0; ultimoSinal.current = Date.now(); setConectado(true); };
    s.onmessage = (ev) => { if (ws.current !== s) return; ultimoSinal.current = Date.now(); try { onEvent(JSON.parse(ev.data)); } catch (e) { /* ignora */ } };
    s.onclose = () => {
      if (ws.current !== s || !vivo.current) return;
      setConectado(false);
      tentativas.current += 1;
      setTimeout(connect, Math.min(15000, 800 * 2 ** Math.min(tentativas.current, 4)));
    };
    s.onerror = () => { try { s.close(); } catch (e) { /* ignora */ } };
  }, [load, onEvent]);

  useEffect(() => {
    vivo.current = true;
    load();
    connect();
    const ping = setInterval(() => { if (ws.current?.readyState === 1) ws.current.send(JSON.stringify({ t: 'ping' })); }, 20000);
    const vigia = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      const mudo = Date.now() - ultimoSinal.current > 50000;
      if (ws.current?.readyState !== 1 || mudo) { ultimoSinal.current = Date.now(); connect(); }
      load();
    }, 15000);
    const onVis = () => { if (document.visibilityState === 'visible') { load(); if (ws.current?.readyState !== 1) connect(); } };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      vivo.current = false;
      clearInterval(ping); clearInterval(vigia);
      document.removeEventListener('visibilitychange', onVis);
      try { ws.current?.close(); } catch (e) { /* ignora */ }
    };
  }, [load, connect]);

  /* Estourar: o servidor sorteia; o cliente só anima. Reenvio com o mesmo id devolve o mesmo balão. */
  const pop = useCallback(async () => {
    const id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    try {
      const { data } = await wbApi.post('/pop', { client_request_id: id });
      setState((s) => (s?.me ? { ...s, me: { ...s.me, balance: Number(data.balance ?? s.me.balance), pops_total: s.me.pops_total + 1,
        pops_available: Math.floor(Number(data.balance ?? s.me.balance) / Math.max(1, s.config?.pop_cost || 500)),
        prizes_total: Number(s.me.prizes_total || 0) + Number(data.pop?.prize || 0) } } : s));
      setRankTick((t) => t + 1);
      return { ok: true, ...data };
    } catch (e) {
      const d = e.response?.data?.detail;
      return { ok: false, reason: d?.reason || 'erro', message: wbError(e) };
    }
  }, []);

  const clearGolden = useCallback(() => setGoldenWin(null), []);
  return { state, loading, erro, conectado, feed, rankTick, goldenWin, clearGolden, reload: load, pop };
};
